import React from 'react';
import { Box, Typography, TextField } from '@mui/material';

import { convertToHalfWidthKatakana } from '../../utils/validations/common-validation';
import { ConstructionCompanyRegistrationForm } from '../../types/company.types';

import { getStylesForEditableField } from '@/utils/helpers';
import { PaperBoxStyles } from '@/components/styles';

interface ConstructionInfoSectionProps {
  formData: ConstructionCompanyRegistrationForm;
  originalFormData: ConstructionCompanyRegistrationForm | null;
  onUpdateField: (field: keyof ConstructionCompanyRegistrationForm, value: unknown) => void;
  getFieldError: (fieldName: string) => string | undefined;
  isFieldValid: (fieldName: string) => boolean;
}

export const ConstructionInfoSection: React.FC<ConstructionInfoSectionProps> = ({
  formData,
  originalFormData,
  onUpdateField,
  getFieldError,
  isFieldValid,
}) => {
  return (
    <PaperBoxStyles elevation={1} sx={{ mb: 3, paddingBottom: '32px' }}>
      <Typography
        variant='h5'
        sx={{
          color: '#1A1C20',
          fontSize: '22px',
          fontWeight: 600,
          mb: 3,
        }}
      >
        建設会社基本情報
      </Typography>

      <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
        *は必須項目です。
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Gali Customer Code */}
        <TextField
          fullWidth
          label='Gali取引先コード'
          placeholder='Gali取引先コードを入力'
          value={formData.galiCustomerCode || ''}
          onChange={(e) => onUpdateField('galiCustomerCode', e.target.value)}
          error={!isFieldValid('galiCustomerCode')}
          helperText={getFieldError('galiCustomerCode')}
          slotProps={{
            input: {
              inputProps: {
                maxLength: 9,
                pattern: '[0-9]*',
                inputMode: 'numeric',
              },
              sx: getStylesForEditableField(
                !!originalFormData,
                originalFormData?.galiCustomerCode,
                formData.galiCustomerCode
              ),
            },
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: '#E0E0E0',
              },
              '&:hover fieldset': {
                borderColor: '#1976d2',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#1976d2',
              },
            },
          }}
        />

        {/* Official Name */}
        <TextField
          fullWidth
          required
          label='正式名称'
          placeholder='正式名称を入力'
          value={formData.officialName}
          onChange={(e) => onUpdateField('officialName', e.target.value)}
          error={!isFieldValid('officialName')}
          helperText={getFieldError('officialName')}
          slotProps={{
            input: {
              inputProps: { maxLength: 100 },
              sx: getStylesForEditableField(
                !!originalFormData,
                originalFormData?.officialName,
                formData.officialName
              ),
            },
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: '#E0E0E0',
              },
              '&:hover fieldset': {
                borderColor: '#1976d2',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#1976d2',
              },
            },
          }}
        />

        {/* Abbreviated Name */}
        <TextField
          fullWidth
          required
          label='略称 (簡易名称)'
          placeholder='略称(簡易名称)を入力'
          value={formData.abbreviatedName}
          onChange={(e) => onUpdateField('abbreviatedName', e.target.value)}
          error={!isFieldValid('abbreviatedName')}
          helperText={getFieldError('abbreviatedName')}
          slotProps={{
            input: {
              inputProps: { maxLength: 100 },
              sx: getStylesForEditableField(
                !!originalFormData,
                originalFormData?.abbreviatedName,
                formData.abbreviatedName
              ),
            },
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: '#E0E0E0',
              },
              '&:hover fieldset': {
                borderColor: '#1976d2',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#1976d2',
              },
            },
          }}
        />

        {/* Customer Name Katakana */}
        <TextField
          fullWidth
          required
          label='取引先名カナ (半角カナ)'
          placeholder='取引先名カナ(半角ｶﾅ)を入力'
          value={formData.nameKatakana || ''}
          onChange={(e) => {
            const convertedValue = convertToHalfWidthKatakana(e.target.value);
            onUpdateField('nameKatakana', convertedValue);
          }}
          onBlur={(e) => {
            const currentValue = e.target.value;
            const convertedValue = convertToHalfWidthKatakana(currentValue);
            if (convertedValue !== currentValue) {
              // Use setTimeout to ensure the update happens after the blur event
              setTimeout(() => {
                onUpdateField('nameKatakana', convertedValue);
              }, 0);
            }
          }}
          error={!isFieldValid('nameKatakana')}
          helperText={getFieldError('nameKatakana')}
          slotProps={{
            input: {
              inputProps: { maxLength: 20 },
              sx: getStylesForEditableField(
                !!originalFormData,
                originalFormData?.nameKatakana,
                formData.nameKatakana
              ),
            },
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: '#E0E0E0',
              },
              '&:hover fieldset': {
                borderColor: '#1976d2',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#1976d2',
              },
            },
          }}
        />
      </Box>
    </PaperBoxStyles>
  );
};
